/* Filling in what the book was worth before it was keeping the record.
 *
 * §9.2 is blunt about this: there is no way to get history back from a broker,
 * so anything from before the book existed has to be typed in. One point at a
 * time through the settings form is fine for the three values somebody
 * remembers and hopeless for two years of them.
 *
 * What arrives this way is marked `seeded`, which the chart already draws
 * differently from a measurement and never lets pass for one. That flag is the
 * whole reason this is allowed to exist: a reconstructed past that announces
 * itself is a useful shape, and one that pretends to be recorded is a lie the
 * chart would repeat every time it was opened.
 *
 * The one rule enforced here rather than left to care: **a recorded day wins.**
 * Nothing imported can land on or after the first day the book measured itself,
 * because that is the part that actually happened, and a traced line laid over
 * it would replace a fact with an estimate. The importer reports what it
 * refused rather than quietly dropping it. */

import type { Snapshot } from '../types';
import { parseDelimited } from '../lib/csv';
import { csvBlock } from './importCashFlows';

export interface ValuePoint {
  at: string;
  totalUsd: number;
}

export interface ValueImport {
  points: ValuePoint[];
  /** Rows that fall on or after the first measured day, and are left alone. */
  afterRecord: ValuePoint[];
  skipped: Array<{ line: number; why: string }>;
}

function toNumber(raw: string | undefined): number | null {
  if (raw === undefined) return null;
  const cleaned = raw.replace(/[\s$ ]/g, '').replace(/,(\d{3}\b)/g, '$1').replace(',', '.');
  if (cleaned === '') return null;
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : null;
}

/** The first day the book measured itself. Reconstructed points do not count —
    they are not measurements, so they are not what has to be protected. */
export function firstRecorded(existing: Snapshot[]): string | null {
  const measured = existing
    .filter((s) => !s.reconstructed && !s.seeded)
    .map((s) => s.at)
    .sort();
  return measured[0] ?? null;
}

export function readValues(text: string, existing: Snapshot[] = []): ValueImport {
  const rows = parseDelimited(csvBlock(text)).filter((r) => r.some((c) => c.trim() !== ''));
  if (rows.length < 2) return { points: [], afterRecord: [], skipped: [] };

  const head = rows[0]!.map((c) => c.trim().toLowerCase());
  const iDate = head.findIndex((h) => h === 'date' || h === 'at');
  const iValue = head.findIndex((h) => h === 'value_usd' || h === 'value' || h === 'total_usd');
  if (iDate < 0 || iValue < 0) return { points: [], afterRecord: [], skipped: [] };

  const cutoff = firstRecorded(existing);
  const points: ValuePoint[] = [];
  const afterRecord: ValuePoint[] = [];
  const skipped: ValueImport['skipped'] = [];
  const seen = new Set<string>();

  rows.slice(1).forEach((row, i) => {
    const line = i + 2;
    const day = (row[iDate] ?? '').trim();
    const value = toNumber(row[iValue]);

    if (!/^\d{4}-\d{2}-\d{2}$/.test(day)) {
      return void skipped.push({ line, why: `"${day}" is not a date` });
    }
    if (value === null) return void skipped.push({ line, why: 'no value on the row' });
    if (value < 0) return void skipped.push({ line, why: 'a book cannot be worth less than nothing' });

    const at = `${day}T12:00:00.000Z`;
    if (seen.has(at)) return void skipped.push({ line, why: `${day} appears twice` });
    seen.add(at);

    const point = { at, totalUsd: value };
    if (cutoff !== null && at >= cutoff) afterRecord.push(point);
    else points.push(point);
  });

  points.sort((a, b) => a.at.localeCompare(b.at));
  return { points, afterRecord, skipped };
}

/* The records as the book stores them.
 *
 * `contributedUsd` is left at zero rather than filled in. The money-paid-in
 * line is derived from the cash flows for every date, including dates that
 * predate them being entered — `buildTimeline` does that — so a figure written
 * here would be a second, staler copy of something already known, and the two
 * would disagree the moment a deposit was corrected. */
export function toSnapshots(points: ValuePoint[]): Snapshot[] {
  return points.map((p) => ({
    at: p.at,
    totalUsd: p.totalUsd,
    contributedUsd: 0,
    seeded: true,
  }));
}
